import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FiUpload, FiImage, FiEdit3, FiDownload, FiLoader, FiWatch, FiLogIn, FiSave, FiSmartphone } from 'react-icons/fi'
import LoginModal from '@/components/LoginModal'
import { useStore } from '@/stores/useStore'
import {
  createImageTo3DTask,
  createMultiImageTo3DTask,
  createTextTo3DTask,
  getTaskStatus,
  saveModel,
  cacheModel,
} from '@/services/api'
import Model3DViewer from '@/components/Model3DViewer'

type GenerationMode = 'image' | 'text' | 'multiimage'

export default function Create() {
  const { t } = useTranslation()
  const {
    isAuthenticated,
    currentModel,
    setCurrentModel,
    generationStatus,
    setGenerationStatus,
    generationProgress,
    setGenerationProgress,
    error,
    setError,
    addHistory,
  } = useStore()

  const [mode, setMode] = useState<GenerationMode>('image')
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [prompt, setPrompt] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [lastTaskId, setLastTaskId] = useState<string | null>(null)
  const [lastThumbnailUrl, setLastThumbnailUrl] = useState<string | null>(null)
  const [showLoginPrompt, setShowLoginPrompt] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)

  // Auto-save model after user logs in
  useEffect(() => {
    const autoSaveAfterLogin = async () => {
      if (isAuthenticated && showLoginPrompt && currentModel && lastTaskId) {
        try {
          await saveModel({
            task_id: lastTaskId,
            model_url: currentModel,
            thumbnail_url: lastThumbnailUrl || '',
            model_name: prompt || 'Generated Model',
            prompt,
          })
          console.log('Model auto-saved after login')
          setShowLoginPrompt(false)
        } catch (err) {
          console.error('Failed to auto-save model after login:', err)
        }
      }
    }
    autoSaveAfterLogin()
  }, [isAuthenticated])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const multiFileInputRef = useRef<HTMLInputElement>(null)

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Convert to base64 data URL for preview
    const reader = new FileReader()
    reader.onload = () => {
      setImageUrl(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleMultiImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    const urls: string[] = []
    for (let i = 0; i < Math.min(files.length, 4); i++) {
      const reader = new FileReader()
      reader.onload = () => {
        urls.push(reader.result as string)
        if (urls.length === Math.min(files.length, 4)) {
          setImageUrls(urls)
        }
      }
      reader.readAsDataURL(files[i])
    }
  }

  const pollTaskStatus = async (taskId: string, taskMode: GenerationMode): Promise<void> => {
    const maxAttempts = 120 // 10 minutes max
    let attempts = 0

    while (attempts < maxAttempts) {
      try {
        const status = await getTaskStatus(taskId, taskMode)
        
        if (status.status === 'SUCCEEDED') {
          const meshyModelUrl = status.model_urls?.glb || status.model_url
          const meshyThumbnail = status.thumbnail_url

          if (meshyModelUrl) {
            // Cache model to local storage first (download from Meshy CDN)
            setGenerationStatus('正在缓存模型到本地...')
            let localModelUrl = meshyModelUrl
            let localThumbnailUrl = meshyThumbnail

            try {
              const cacheResult = await cacheModel({
                task_id: taskId,
                model_url: meshyModelUrl,
                thumbnail_url: meshyThumbnail || '',
              })
              localModelUrl = cacheResult.model_url
              localThumbnailUrl = cacheResult.thumbnail_url
              console.log('Model cached to local storage:', localModelUrl)
            } catch (cacheErr) {
              console.error('Failed to cache model, using Meshy URL:', cacheErr)
              // Fall back to Meshy URL if caching fails
            }

            setCurrentModel(localModelUrl)
            setLastThumbnailUrl(localThumbnailUrl || null)
            setGenerationStatus('完成')
            setGenerationProgress(100)

            // Add to local history (works without login)
            const historyItem = {
              id: `local_${Date.now()}`,
              taskId,
              modelUrl: localModelUrl,
              thumbnailUrl: localThumbnailUrl,
              timestamp: new Date().toISOString(),
              mode: taskMode,
              name: prompt || 'Generated Model',
            }
            addHistory(historyItem)

            // If authenticated, also save to server database
            if (isAuthenticated) {
              try {
                await saveModel({
                  task_id: taskId,
                  model_url: localModelUrl,
                  thumbnail_url: localThumbnailUrl || '',
                  model_name: prompt || 'Generated Model',
                  prompt,
                })
              } catch (saveErr) {
                console.error('Failed to save model to server:', saveErr)
              }
            } else {
              // Show login prompt for unauthenticated users
              setShowLoginPrompt(true)
            }
          }
          return
        } else if (status.status === 'FAILED') {
          throw new Error(t('create.errors.generationFailed'))
        } else {
          // Still processing
          const progress = status.progress || Math.min(attempts * 2, 90)
          setGenerationProgress(progress)
          setGenerationStatus(t('create.progress.generating'))
        }

        await new Promise((resolve) => setTimeout(resolve, 5000))
        attempts++
      } catch (err) {
        throw err
      }
    }

    throw new Error('Generation timeout')
  }

  const handleGenerate = async () => {
    setError(null)
    setIsGenerating(true)
    setGenerationProgress(0)
    setCurrentModel(null)

    try {
      let taskId: string

      if (mode === 'image') {
        if (!imageUrl) {
          setError(t('create.errors.uploadImage'))
          return
        }
        setGenerationStatus(t('create.progress.uploading'))
        const result = await createImageTo3DTask(imageUrl)
        taskId = result.result
      } else if (mode === 'multiimage') {
        if (imageUrls.length === 0) {
          setError(t('create.errors.uploadImage'))
          return
        }
        setGenerationStatus(t('create.progress.uploading'))
        const result = await createMultiImageTo3DTask(imageUrls)
        taskId = result.result
      } else {
        if (!prompt.trim()) {
          setError('请输入描述文字')
          return
        }
        setGenerationStatus(t('create.progress.processing'))
        const result = await createTextTo3DTask(prompt)
        taskId = result.result
      }

      setLastTaskId(taskId)
      setGenerationStatus(t('create.progress.generating'))
      await pollTaskStatus(taskId, mode)
    } catch (err: any) {
      console.error('Generation error:', err)
      setError(err.message || t('create.errors.generationFailed'))
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-serif font-bold text-white mb-8">{t('create.title')}</h1>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Panel - Input */}
          <div className="space-y-6">
            {/* Mode Selection */}
            <div className="bg-luxury-navy-900/50 backdrop-blur-sm rounded-lg border border-luxury-gold-800/30 p-6">
              <h2 className="text-xl font-semibold text-white mb-4">选择模式</h2>
              <div className="flex space-x-4">
                <button
                  onClick={() => setMode('image')}
                  className={`flex-1 py-3 px-4 rounded-lg border transition-colors ${
                    mode === 'image'
                      ? 'bg-luxury-gold-600 border-luxury-gold-500 text-luxury-navy-950'
                      : 'border-luxury-gold-800/30 text-gray-300 hover:border-luxury-gold-500/50'
                  }`}
                >
                  <FiImage className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm">{t('create.modes.image')}</span>
                </button>
                <button
                  onClick={() => setMode('multiimage')}
                  className={`flex-1 py-3 px-4 rounded-lg border transition-colors ${
                    mode === 'multiimage'
                      ? 'bg-luxury-gold-600 border-luxury-gold-500 text-luxury-navy-950'
                      : 'border-luxury-gold-800/30 text-gray-300 hover:border-luxury-gold-500/50'
                  }`}
                >
                  <FiImage className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm">多图转3D</span>
                </button>
                <button
                  onClick={() => setMode('text')}
                  className={`flex-1 py-3 px-4 rounded-lg border transition-colors ${
                    mode === 'text'
                      ? 'bg-luxury-gold-600 border-luxury-gold-500 text-luxury-navy-950'
                      : 'border-luxury-gold-800/30 text-gray-300 hover:border-luxury-gold-500/50'
                  }`}
                >
                  <FiEdit3 className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm">{t('create.modes.text')}</span>
                </button>
              </div>
            </div>

            {/* Input Area */}
            <div className="bg-luxury-navy-900/50 backdrop-blur-sm rounded-lg border border-luxury-gold-800/30 p-6">
              {mode === 'image' && (
                <div>
                  <h2 className="text-xl font-semibold text-white mb-4">{t('create.uploadImage')}</h2>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-luxury-gold-800/50 rounded-lg p-8 text-center cursor-pointer hover:border-luxury-gold-500/50 transition-colors"
                  >
                    {imageUrl ? (
                      <img src={imageUrl} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
                    ) : (
                      <>
                        <FiUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-400">点击或拖拽上传图片</p>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
              )}

              {mode === 'multiimage' && (
                <div>
                  <h2 className="text-xl font-semibold text-white mb-4">上传多张图片 (最多4张)</h2>
                  <div
                    onClick={() => multiFileInputRef.current?.click()}
                    className="border-2 border-dashed border-luxury-gold-800/50 rounded-lg p-8 text-center cursor-pointer hover:border-luxury-gold-500/50 transition-colors"
                  >
                    {imageUrls.length > 0 ? (
                      <div className="grid grid-cols-2 gap-4">
                        {imageUrls.map((url, i) => (
                          <img key={i} src={url} alt={`Preview ${i}`} className="max-h-32 mx-auto rounded-lg" />
                        ))}
                      </div>
                    ) : (
                      <>
                        <FiUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-400">点击上传多张图片</p>
                      </>
                    )}
                  </div>
                  <input
                    ref={multiFileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleMultiImageUpload}
                    className="hidden"
                  />
                </div>
              )}

              {mode === 'text' && (
                <div>
                  <h2 className="text-xl font-semibold text-white mb-4">{t('create.generatePrompt')}</h2>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="描述您想要生成的3D模型..."
                    className="w-full h-32 px-4 py-3 bg-luxury-navy-800 border border-luxury-gold-800/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-luxury-gold-500/50 resize-none"
                  />
                </div>
              )}

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="w-full mt-6 py-4 bg-gradient-to-r from-luxury-gold-600 to-luxury-gold-500 text-luxury-navy-950 font-semibold rounded-lg hover:from-luxury-gold-500 hover:to-luxury-gold-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isGenerating ? (
                  <>
                    <FiLoader className="w-5 h-5 animate-spin" />
                    <span>{t('create.button.generating')}</span>
                  </>
                ) : (
                  <span>{t('create.button.generate')}</span>
                )}
              </button>

              {/* Progress */}
              {isGenerating && (
                <div className="mt-6">
                  <div className="flex justify-between text-sm text-gray-400 mb-2">
                    <span>{generationStatus}</span>
                    <span>{generationProgress}%</span>
                  </div>
                  <div className="w-full bg-luxury-navy-800 rounded-full h-2">
                    <div
                      className="bg-luxury-gold-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${generationProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="mt-4 p-4 bg-red-900/50 border border-red-800/30 rounded-lg">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="bg-luxury-navy-900/50 backdrop-blur-sm rounded-lg border border-luxury-gold-800/30 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">{t('create.preview.title')}</h2>
              {currentModel && (
                <div className="flex space-x-2">
                  {/* 一键试戴按钮 */}
                  <a
                    href={`/Mobile_AR/wrist-test.html?model=${encodeURIComponent(currentModel)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-lg hover:from-purple-500 hover:to-purple-400 transition-colors"
                    title="AR 试戴"
                  >
                    <FiWatch className="w-4 h-4" />
                    <span>一键试戴</span>
                  </a>
                  {/* 下载按钮 */}
                  <a
                    href={currentModel}
                    download
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-luxury-gold-600 to-luxury-gold-500 text-luxury-navy-950 rounded-lg hover:from-luxury-gold-500 hover:to-luxury-gold-400 transition-colors"
                  >
                    <FiDownload className="w-4 h-4" />
                    <span>{t('common.download')}</span>
                  </a>
                </div>
              )}
            </div>
            <div className="h-[600px] rounded-lg overflow-hidden bg-luxury-navy-800 relative">
              <Model3DViewer modelUrl={currentModel} />
              
              {/* Login Prompt Overlay for unauthenticated users */}
              {showLoginPrompt && !isAuthenticated && currentModel && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-20">
                  <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border-2 border-yellow-500/50 p-6 mx-4 max-w-md shadow-2xl shadow-yellow-500/20">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FiSave className="w-8 h-8 text-yellow-400" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3">登录以永久保存模型</h3>
                      <p className="text-gray-200 text-sm mb-5">
                        您的 3D 模型已生成成功！登录后可享受：
                      </p>
                      <ul className="text-sm text-white space-y-3 mb-6 text-left">
                        <li className="flex items-center space-x-3 bg-white/10 rounded-lg px-4 py-2">
                          <FiSave className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                          <span>永久保存模型到云端</span>
                        </li>
                        <li className="flex items-center space-x-3 bg-white/10 rounded-lg px-4 py-2">
                          <FiSmartphone className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                          <span>跨设备随时访问您的作品</span>
                        </li>
                      </ul>
                      <div className="flex flex-col space-y-3">
                        <button
                          onClick={() => setShowLoginModal(true)}
                          className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-yellow-400 text-slate-900 font-bold rounded-xl hover:from-yellow-400 hover:to-yellow-300 transition-all shadow-lg shadow-yellow-500/30"
                        >
                          <FiLogIn className="w-5 h-5" />
                          <span>立即登录</span>
                        </button>
                        <button
                          onClick={() => setShowLoginPrompt(false)}
                          className="w-full px-6 py-2 text-gray-300 hover:text-white transition-colors border border-gray-600 rounded-xl hover:border-gray-400"
                        >
                          稍后再说
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Login Modal */}
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </div>
  )
}
